import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useToast } from "../../../shared/hooks/useToast";
import theme from "@shared/utils/themeColors";

/**
 * Success page after completing a subscription payment
 */
const SubscriptionSuccessPage: React.FC = () => {
  const [isProcessing, setIsProcessing] = useState(true);
  const [processingComplete, setProcessingComplete] = useState(false);
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (processingComplete) return;

    const processPayment = async () => {
      try {
        // Get the session ID from the URL query parameters
        const params = new URLSearchParams(location.search);
        const sessionId = params.get("session_id");

        if (!sessionId) {
          showToast("Invalid session. Redirecting to dashboard...", "error");
          setTimeout(() => navigate("/dashboard"), 3000);
          setProcessingComplete(true);
          return;
        }

        // Payment was successful
        showToast(
          "Payment successful! Your subscription has been activated.",
          "success"
        );

        // Wait a moment before redirecting to allow the user to see the success message
        setTimeout(() => {
          setIsProcessing(false);
          navigate("/settings/plans");
        }, 3000);

        setProcessingComplete(true);
      } catch (error) {
        console.error("Error processing payment confirmation:", error);
        showToast(
          "There was an error processing your payment confirmation.",
          "error"
        );
        setIsProcessing(false);
        setProcessingComplete(true);
      }
    };

    processPayment();
  }, [location, navigate, showToast, processingComplete]);

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900 p-4">
      <div className={`w-full max-w-md p-8 space-y-8 ${theme.bg.primary} rounded-lg shadow-md`}>
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            Payment Successful!
          </h1>

          <div className="flex justify-center mb-4">
            {isProcessing ? (
              <svg
                className="animate-spin h-10 w-10 text-primary"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            ) : (
              <svg
                className={`h-16 w-16 ${theme.text.success}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 13l4 4L19 7"
                ></path>
              </svg>
            )}
          </div>

          <p className="text-gray-600 dark:text-gray-300 mb-4">
            {isProcessing
              ? "Processing your payment..."
              : "Your subscription has been activated successfully!"}
          </p>

          <p className={theme.text.muted}>
            {isProcessing
              ? "Please wait while we confirm your payment..."
              : "You will be redirected to your subscription page..."}
          </p>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionSuccessPage;
